import { createContext, useContext, useState, useCallback } from 'react';
import Confetti from 'react-confetti';
import { useWindowSize } from '@/hooks/useWindowSize';

const CelebrationContext = createContext(null);

// Affirming words that appear during celebrations
const AFFIRMING_WORDS = [
  "You just did a brave thing.",
  "That boundary? Blessed and confirmed.",
  "Soul check-in complete.",
  "Added to your joy bank.",
  "Sisterhood logged.",
  "That 'no' just made room for your 'yes.'",
  "You're not behind. You're becoming.",
  "You deserve this moment.",
  "Exhale here.",
  "You showed up for yourself.",
  "This is your soft power.",
  "Rest is your birthright.",
  "You are seen. You are loved.",
  "Welcome home, Sis."
];

// Grand celebration messages for big moments
const GRAND_MESSAGES = {
  milestone: [
    "Look at you, Sis! You're glowing.",
    "This is what becoming looks like.",
    "You've unlocked a new level of you.",
    "Your ancestors are celebrating with you."
  ],
  firstPost: [
    "Your voice matters here.",
    "The sisterhood heard you.",
    "That took courage. We see you."
  ],
  sessionBooked: [
    "Investing in yourself? That's power.",
    "Your transformation is scheduled.",
    "Self-care on the calendar? Yes, Sis!"
  ],
  profileComplete: [
    "Now we really see you.",
    "Your story is powerful.",
    "Authenticity unlocked."
  ],
  focusPathComplete: [
    "90 days of becoming? Complete!",
    "You did what most won't even start.",
    "Your dedication is inspiring."
  ]
};

export const CelebrationProvider = ({ children }) => {
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);
  const [showHearts, setShowHearts] = useState(false);
  const [showGrandConfetti, setShowGrandConfetti] = useState(false);
  const [showSparkleRain, setShowSparkleRain] = useState(false);
  const [affirmingWord, setAffirmingWord] = useState('');
  const [celebrationSize, setCelebrationSize] = useState('normal'); // 'normal', 'grand', 'epic'

  // Trigger a celebration
  const celebrate = useCallback((type = 'confetti', customMessage = null) => {
    setCelebrationSize('normal');
    
    // Set affirming word
    const word = customMessage || AFFIRMING_WORDS[Math.floor(Math.random() * AFFIRMING_WORDS.length)];
    setAffirmingWord(word);
    
    if (type === 'hearts') {
      setShowHearts(true);
      setTimeout(() => setShowHearts(false), 4000);
    } else {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
    }
    
    // Clear affirming word after animation
    setTimeout(() => setAffirmingWord(''), 3500);
  }, []);

  // Grand celebration for big moments (more confetti, longer duration)
  const grandCelebrate = useCallback((type = 'confetti', messageCategory = 'milestone') => {
    setCelebrationSize('grand');
    
    // Get message from category or use random
    const messages = GRAND_MESSAGES[messageCategory] || GRAND_MESSAGES.milestone;
    const word = messages[Math.floor(Math.random() * messages.length)];
    setAffirmingWord(word);
    
    if (type === 'hearts') {
      setShowHearts(true);
      setTimeout(() => setShowHearts(false), 6000);
    } else if (type === 'sparkle') {
      setShowSparkleRain(true);
      setTimeout(() => setShowSparkleRain(false), 6000);
    } else {
      setShowGrandConfetti(true);
      setTimeout(() => setShowGrandConfetti(false), 6000);
    }
    
    setTimeout(() => setAffirmingWord(''), 5500);
  }, []);

  // Epic celebration for major milestones (confetti + hearts + sparkles)
  const epicCelebrate = useCallback((customMessage = null) => {
    setCelebrationSize('epic');
    
    const word = customMessage || "This is YOUR moment, Sis! 👑";
    setAffirmingWord(word);
    
    // Start all animations with slight delays for layered effect
    setShowGrandConfetti(true);
    setTimeout(() => setShowHearts(true), 500);
    setTimeout(() => setShowSparkleRain(true), 1000);
    
    // Clear all after 8 seconds
    setTimeout(() => {
      setShowGrandConfetti(false);
      setShowHearts(false);
      setShowSparkleRain(false);
    }, 8000);
    
    setTimeout(() => setAffirmingWord(''), 7500);
  }, []);

  // Quick celebration for small wins (just the overlay, no confetti)
  const quickCelebrate = useCallback((message = null) => {
    const word = message || AFFIRMING_WORDS[Math.floor(Math.random() * AFFIRMING_WORDS.length)];
    setAffirmingWord(word);
    setTimeout(() => setAffirmingWord(''), 2500);
  }, []);

  // Confetti colors - brand colors
  const confettiColors = ['#2D5A4A', '#C4684F', '#F5E6D3', '#E8D5C4', '#9B59B6', '#FFD700'];
  const heartColors = ['#C4684F', '#FF6B6B', '#FFB6C1', '#FF69B4', '#E91E63'];
  const sparkleColors = ['#FFD700', '#FFA500', '#FFFF00', '#E8D5C4', '#FFFFFF'];
  const diamondColors = ['#B9F2FF', '#87CEEB', '#00CED1', '#FFD700', '#FFFFFF', '#E0FFFF'];
  
  // Confetti piece counts based on celebration size
  const getPieceCount = (base) => {
    if (celebrationSize === 'epic') return base * 3;
    if (celebrationSize === 'grand') return base * 2;
    return base;
  };

  // Diamond explosion celebration
  const diamondCelebrate = useCallback((customMessage = null) => {
    setCelebrationSize('grand');
    
    const word = customMessage || "Diamond earned! You're sparkling, Sis! 💎";
    setAffirmingWord(word);
    
    setShowSparkleRain(true);
    setTimeout(() => setShowGrandConfetti(true), 300);
    
    setTimeout(() => {
      setShowSparkleRain(false);
      setShowGrandConfetti(false);
    }, 5000);
    
    setTimeout(() => setAffirmingWord(''), 4500);
  }, []);

  return (
    <CelebrationContext.Provider value={{ celebrate, grandCelebrate, epicCelebrate, quickCelebrate, diamondCelebrate }}>
      {children}
      
      {/* Normal Confetti Animation */}
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={getPieceCount(200)}
          colors={confettiColors}
          gravity={0.3}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999, pointerEvents: 'none' }}
        />
      )}
      
      {/* Grand Confetti Animation (more pieces, slower) */}
      {showGrandConfetti && (
        <Confetti
          width={width}
          height={height}
          recycle={celebrationSize === 'epic'}
          numberOfPieces={getPieceCount(400)}
          colors={confettiColors}
          gravity={0.2}
          wind={0.01}
          initialVelocityY={20}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999, pointerEvents: 'none' }}
        />
      )}
      
      {/* Hearts Animation */}
      {showHearts && (
        <Confetti
          width={width}
          height={height}
          recycle={celebrationSize === 'epic'}
          numberOfPieces={getPieceCount(150)}
          colors={heartColors}
          gravity={0.15}
          drawShape={ctx => {
            ctx.beginPath();
            const scale = 0.15;
            ctx.moveTo(75 * scale, 40 * scale);
            ctx.bezierCurveTo(75 * scale, 37 * scale, 70 * scale, 25 * scale, 50 * scale, 25 * scale);
            ctx.bezierCurveTo(20 * scale, 25 * scale, 20 * scale, 62.5 * scale, 20 * scale, 62.5 * scale);
            ctx.bezierCurveTo(20 * scale, 80 * scale, 40 * scale, 102 * scale, 75 * scale, 120 * scale);
            ctx.bezierCurveTo(110 * scale, 102 * scale, 130 * scale, 80 * scale, 130 * scale, 62.5 * scale);
            ctx.bezierCurveTo(130 * scale, 62.5 * scale, 130 * scale, 25 * scale, 100 * scale, 25 * scale);
            ctx.bezierCurveTo(85 * scale, 25 * scale, 75 * scale, 37 * scale, 75 * scale, 40 * scale);
            ctx.fill();
          }}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9998, pointerEvents: 'none' }}
        />
      )}
      
      {/* Sparkle/Star Rain Animation */}
      {showSparkleRain && (
        <Confetti
          width={width}
          height={height}
          recycle={celebrationSize === 'epic'}
          numberOfPieces={getPieceCount(100)}
          colors={sparkleColors}
          gravity={0.1}
          wind={0.02}
          drawShape={ctx => {
            // Draw a star shape
            const spikes = 5;
            const outerRadius = 12;
            const innerRadius = 6;
            let rot = Math.PI / 2 * 3;
            let x = 0;
            let y = 0;
            const step = Math.PI / spikes;

            ctx.beginPath();
            ctx.moveTo(0, -outerRadius);
            for (let i = 0; i < spikes; i++) {
              x = Math.cos(rot) * outerRadius;
              y = Math.sin(rot) * outerRadius;
              ctx.lineTo(x, y);
              rot += step;

              x = Math.cos(rot) * innerRadius;
              y = Math.sin(rot) * innerRadius;
              ctx.lineTo(x, y);
              rot += step;
            }
            ctx.lineTo(0, -outerRadius);
            ctx.closePath();
            ctx.fill();
          }}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9997, pointerEvents: 'none' }}
        />
      )}
      
      {/* Affirming Word Overlay */}
      {affirmingWord && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-[10000] pointer-events-none"
          style={{ animation: celebrationSize === 'epic' ? 'fadeInOutSlow 7s ease-in-out' : 'fadeInOut 3s ease-in-out' }}
        >
          <div className={`bg-white/95 backdrop-blur-sm px-8 py-6 rounded-2xl shadow-2xl border-2 max-w-md mx-4 text-center
            ${celebrationSize === 'epic' ? 'border-yellow-400 scale-110' : 
              celebrationSize === 'grand' ? 'border-terracotta/40' : 'border-terracotta/20'}
            animate-bounce-gentle`}
          >
            <p className={`font-heading leading-relaxed
              ${celebrationSize === 'epic' ? 'text-3xl text-forest' : 
                celebrationSize === 'grand' ? 'text-2xl text-forest' : 'text-2xl text-forest'}`}
            >
              {affirmingWord}
            </p>
            <div className="mt-3 flex justify-center gap-2">
              {celebrationSize === 'epic' ? (
                <>
                  <span className="text-3xl">👑</span>
                  <span className="text-3xl">💎</span>
                  <span className="text-3xl">✨</span>
                  <span className="text-3xl">💎</span>
                  <span className="text-3xl">👑</span>
                </>
              ) : celebrationSize === 'grand' ? (
                <>
                  <span className="text-2xl">💎</span>
                  <span className="text-2xl">✨</span>
                  <span className="text-2xl">🌟</span>
                  <span className="text-2xl">✨</span>
                  <span className="text-2xl">💎</span>
                </>
              ) : (
                <>
                  <span className="text-2xl">💎</span>
                  <span className="text-2xl">✨</span>
                  <span className="text-2xl">💎</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: scale(0.8); }
          15% { opacity: 1; transform: scale(1); }
          85% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        
        @keyframes fadeInOutSlow {
          0% { opacity: 0; transform: scale(0.7); }
          10% { opacity: 1; transform: scale(1.05); }
          15% { transform: scale(1); }
          85% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }
      `}</style>
    </CelebrationContext.Provider>
  );
};

export const useCelebration = () => {
  const context = useContext(CelebrationContext);
  if (!context) {
    throw new Error('useCelebration must be used within a CelebrationProvider');
  }
  return context;
};
